import type { ProductType, GetProductsResponse } from '@dtp/membership-service-types';
import { produce } from 'immer';
import { Action, createAsyncAction, createReducer } from 'typesafe-actions';
import type { ReduxError } from '@universal/errors';

export enum Actions {
  GET_INTERNATIONAL_DRIVING_PERMIT = 'naf/internationDrivingPermit/GET_INTERNATIONAL_DRIVING_PERMIT',
  GET_INTERNATIONAL_DRIVING_PERMIT_SUCCESS = 'naf/internationDrivingPermit/GET_INTERNATIONAL_DRIVING_PERMIT_SUCCESS',
  GET_INTERNATIONAL_DRIVING_PERMIT_FAIL = 'naf/internationDrivingPermit/GET_INTERNATIONAL_DRIVING_PERMIT_FAIL',
  GET_INTERNATIONAL_DRIVING_PERMIT_CANCEL = 'naf/internationDrivingPermit/GET_INTERNATIONAL_DRIVING_PERMIT_CANCEL',
}

export interface State {
  data: {
    eligibleProducts: Record<ProductType['productNumber'], ProductType> | null;
    originalEligibleProducts: Record<ProductType['productNumber'], ProductType> | null;
    ownedProducts: Record<ProductType['productNumber'], ProductType> | null;
  };
  isUpdating: boolean;
  errorState?: any;
}

export const initialState: State = {
  data: {
    eligibleProducts: null,
    originalEligibleProducts: null,
    ownedProducts: null,
  },
  isUpdating: false,
};

export const actions = {
  getInternationalDrivingPermit: createAsyncAction(
    Actions.GET_INTERNATIONAL_DRIVING_PERMIT, // request payload creator
    Actions.GET_INTERNATIONAL_DRIVING_PERMIT_SUCCESS, // success payload creator
    Actions.GET_INTERNATIONAL_DRIVING_PERMIT_FAIL, // failure payload creator
    Actions.GET_INTERNATIONAL_DRIVING_PERMIT_CANCEL, // optional cancel payload creator
  )<string | undefined, GetProductsResponse, ReduxError, undefined>(),
};

export const reducers = createReducer<State, Action>(initialState, {})
  .handleAction(actions.getInternationalDrivingPermit.request, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.isUpdating = true;
    }),
  )
  .handleAction(actions.getInternationalDrivingPermit.success, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.isUpdating = false;
      const eligibleProducts = (action.payload?.customerProducts?.eligibleProducts ?? []).reduce(
        (acc: Record<ProductType['productNumber'], ProductType>, cur) => {
          acc[cur.productNumber] = cur;
          return acc;
        },
        {},
      );

      const hasCampaignCode = !!action.payload.customerProducts.campaignCodeDetails?.campaignCode;

      if (!hasCampaignCode) {
        draftState.data.originalEligibleProducts = eligibleProducts;
      } else if (!draftState.data.originalEligibleProducts && draftState.data.eligibleProducts) {
        draftState.data.originalEligibleProducts = draftState.data.eligibleProducts;
      }

      draftState.data.eligibleProducts = eligibleProducts;

      draftState.data.ownedProducts = (action.payload?.customerProducts?.ownedProducts ?? []).reduce(
        (acc: Record<ProductType['productNumber'], ProductType>, cur) => {
          acc[cur.productNumber] = cur;
          return acc;
        },
        {},
      );
    }),
  )
  .handleAction(actions.getInternationalDrivingPermit.failure, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.isUpdating = false;
      draftState.errorState = action.payload;
    }),
  )
  .handleAction(actions.getInternationalDrivingPermit.cancel, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.isUpdating = false;
    }),
  );
